import React, { useEffect } from 'react';
import { FaTimes, FaShieldAlt, FaFileContract, FaCookie } from 'react-icons/fa';

const PolicyModal = ({ isOpen, onClose, policyType, onOpenContactForm }) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const policies = {
    privacy: {
      icon: <FaShieldAlt className="text-4xl" />,
      title: 'Privacy Policy',
      gradient: 'from-blue-500 to-primary',
      lastUpdated: 'December 28, 2025',
      sections: [
        {
          title: '1. Information We Collect',
          content: `We collect information that you provide directly to us when you:

• Fill out forms on our website
• Request consultations or services
• Subscribe to our newsletter
• Contact us for support
• Participate in surveys or promotions

This may include your name, email address, phone number, company information, and any other details you choose to provide.`
        },
        {
          title: '2. How We Use Your Information',
          content: `We use the information we collect to:

• Provide, maintain, and improve our services
• Process your requests and transactions
• Send you technical notices and support messages
• Respond to your comments and questions
• Communicate about products, services, and events
• Monitor and analyze trends and usage
• Detect and prevent fraudulent transactions
• Comply with legal obligations`
        },
        {
          title: '3. Information Sharing',
          content: `We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:

• With your consent
• With service providers who assist in our operations
• To comply with legal obligations
• To protect our rights and prevent fraud
• In connection with a business transfer or merger`
        },
        {
          title: '4. Data Security',
          content: `We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no internet transmission is completely secure, and we cannot guarantee absolute security.`
        },
        {
          title: '5. Your Rights',
          content: `You have the right to:

• Access your personal information
• Correct inaccurate data
• Request deletion of your data
• Object to processing
• Request data portability
• Withdraw consent at any time

To exercise these rights, please use our contact form to get in touch with us.`
        },
        {
          title: '6. Cookies and Tracking',
          content: `We use cookies and similar tracking technologies to enhance your experience. See our Cookie Policy for more details.`
        },
        {
          title: '7. Contact Us',
          content: `If you have questions about this Privacy Policy, please use our contact form to reach out to us. We're an online business available worldwide and typically respond within 24 hours.`,
          showContactButton: true
        }
      ]
    },
    terms: {
      icon: <FaFileContract className="text-4xl" />,
      title: 'Terms of Service',
      gradient: 'from-teal to-green-500',
      lastUpdated: 'December 28, 2025',
      sections: [
        {
          title: '1. Acceptance of Terms',
          content: `By accessing and using CitiTekPro's services, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.`
        },
        {
          title: '2. Services Description',
          content: `CitiTekPro provides technology solutions including:

• Web development and design
• IT support and consulting
• Domain registration and management
• Web hosting services
• Custom software development
• Digital transformation consulting

Service specifications, pricing, and availability are subject to change.`
        },
        {
          title: '3. User Obligations',
          content: `You agree to:

• Provide accurate and complete information
• Maintain the security of your account credentials
• Use our services only for lawful purposes
• Not interfere with or disrupt our services
• Comply with all applicable laws and regulations
• Not attempt to gain unauthorized access to our systems`
        },
        {
          title: '4. Intellectual Property',
          content: `All content, features, and functionality of our services are owned by CitiTekPro and protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.`
        },
        {
          title: '5. Payment Terms',
          content: `• Payment is due according to the terms specified in your service agreement
• All fees are non-refundable unless otherwise stated
• We reserve the right to modify pricing with notice
• Late payments may result in service suspension
• You are responsible for all applicable taxes`
        },
        {
          title: '6. Limitation of Liability',
          content: `CitiTekPro shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of our services. Our total liability shall not exceed the amount paid by you for the services in the preceding 12 months.`
        },
        {
          title: '7. Termination',
          content: `We reserve the right to suspend or terminate your access to our services at any time for violation of these terms or for any other reason at our discretion. You may terminate your account by contacting us with written notice.`
        },
        {
          title: '8. Changes to Terms',
          content: `We may modify these Terms of Service at any time. We will notify you of significant changes via email or through our website. Continued use of our services after changes constitutes acceptance of the modified terms.`
        },
        {
          title: '9. Governing Law',
          content: `These terms are governed by the laws of the State of California, USA, without regard to conflict of law provisions. Any disputes shall be resolved in the courts of California.`
        }
      ]
    },
    cookie: {
      icon: <FaCookie className="text-4xl" />,
      title: 'Cookie Policy',
      gradient: 'from-accent to-orange-600',
      lastUpdated: 'December 28, 2025',
      sections: [
        {
          title: '1. What Are Cookies',
          content: `Cookies are small text files stored on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and understanding how you use our site.`
        },
        {
          title: '2. Types of Cookies We Use',
          content: `Essential Cookies:
• Required for the website to function properly
• Enable core functionality like security and accessibility
• Cannot be disabled

Performance Cookies:
• Help us understand how visitors interact with our site
• Collect anonymous information about page visits
• Used to improve website performance

Functionality Cookies:
• Remember your preferences and settings
• Enable personalized features
• Enhance your user experience

Analytics Cookies:
• Track website traffic and user behavior
• Help us improve our services
• Provide insights into usage patterns`
        },
        {
          title: '3. Third-Party Cookies',
          content: `We may use third-party services that set cookies on our behalf:

• Google Analytics - for website analytics
• Social media platforms - for sharing features
• Customer support tools - for chat functionality

These third parties have their own privacy policies governing cookie use.`
        },
        {
          title: '4. Managing Cookies',
          content: `You can control cookies through your browser settings:

Chrome: Settings > Privacy and Security > Cookies
Firefox: Options > Privacy & Security > Cookies
Safari: Preferences > Privacy > Cookies
Edge: Settings > Privacy > Cookies

Note: Disabling cookies may affect website functionality.`
        },
        {
          title: '5. Cookie Duration',
          content: `Session Cookies:
• Temporary cookies deleted when you close your browser
• Used for essential functionality

Persistent Cookies:
• Remain on your device for a set period
• Used to remember preferences
• Typically expire after 1-2 years`
        },
        {
          title: '6. Your Consent',
          content: `By using our website, you consent to our use of cookies as described in this policy. You can withdraw consent at any time by clearing your cookies and adjusting your browser settings.`
        },
        {
          title: '7. Updates to This Policy',
          content: `We may update this Cookie Policy periodically to reflect changes in technology or legislation. Please review this page regularly for updates.`
        },
        {
          title: '8. Contact Information',
          content: `For questions about our use of cookies, please use our contact form to reach out. We're available online and respond quickly to all inquiries.`,
          showContactButton: true
        }
      ]
    }
  };

  const currentPolicy = policies[policyType];

  if (!currentPolicy) return null;

  return (
    <div className="policy-modal-wrapper fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-fade-in">
      {/* Backdrop */}
      <div
        className="policy-modal-backdrop absolute inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal Container */}
      <div className="policy-modal-container relative w-full max-w-4xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden animate-slide-up flex flex-col">
        {/* Header */}
        <div className={`policy-modal-header flex-shrink-0 bg-gradient-to-r ${currentPolicy.gradient} p-6 md:p-8`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl text-white">
                {currentPolicy.icon}
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">
                  {currentPolicy.title}
                </h2>
                <p className="text-white/90 text-sm mt-1">
                  Last Updated: {currentPolicy.lastUpdated}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="policy-modal-close p-2 hover:bg-white/20 rounded-lg transition-all duration-300 text-white group"
              aria-label="Close modal"
            >
              <FaTimes className="text-2xl group-hover:rotate-90 transition-transform duration-300" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="policy-modal-content flex-1 overflow-y-auto p-6 md:p-8">
          <div className="space-y-8">
            {currentPolicy.sections.map((section, index) => (
              <div
                key={index}
                className="policy-section animate-fade-in-up"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-3">
                  <span className={`w-1 h-8 bg-gradient-to-b ${currentPolicy.gradient} rounded-full`}></span>
                  {section.title}
                </h3>
                <div className="pl-7">
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </p>
                  {section.showContactButton && onOpenContactForm && (
                    <button
                      onClick={() => {
                        onClose();
                        onOpenContactForm();
                      }}
                      className="mt-4 px-6 py-3 bg-gradient-to-r from-primary to-teal hover:from-primary-600 hover:to-teal-600 text-white font-semibold rounded-lg transition-all duration-300 shadow-md hover:shadow-lg"
                    >
                      Open Contact Form
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Footer Note */}
          <div className="mt-12 p-6 bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-700 dark:to-gray-600 rounded-2xl border-l-4 border-primary dark:border-teal">
            <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
              <strong>Note:</strong> This {currentPolicy.title.toLowerCase()} is subject to change.
              We will notify you of any significant changes. For questions or concerns, please{' '}
              {onOpenContactForm ? (
                <button
                  onClick={() => {
                    onClose();
                    onOpenContactForm();
                  }}
                  className="text-primary dark:text-teal hover:underline font-semibold"
                >
                  use our contact form
                </button>
              ) : (
                <span className="text-primary dark:text-teal font-semibold">use our contact form</span>
              )}.
            </p>
          </div>
        </div>

        {/* Bottom Action Bar */}
        <div className="policy-modal-footer flex-shrink-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 md:p-6">
          <div className="flex flex-col sm:flex-row gap-3 justify-between items-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              © {new Date().getFullYear()} CitiTekPro. All rights reserved.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-gradient-to-r from-primary to-teal hover:from-primary-600 hover:to-teal-600 text-white font-semibold rounded-lg transition-all duration-300 shadow-md hover:shadow-lg"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyModal;
